/**
 * sharedStripe
 *
 * A single, lazily-created Stripe.js promise shared by the in-place TopUpModal.
 * Mirrors the publishable-key resolution order used by the Wallet page
 * (src/pages/Wallet.tsx `resolveStripePublishableKey`) so both surfaces load
 * the same key from the same sources.
 *
 * loadStripe() is only invoked the first time getSharedStripePromise() is
 * called (i.e. the first time the user opens the top-up modal), so users who
 * never top up never pay the cost of fetching stripe.js.
 */

import { loadStripe } from '@stripe/stripe-js';

/** The resolved value of loadStripe() — kept local to avoid a named type import
 * that isn't exported by this version of @stripe/stripe-js. */
type StripePromise = ReturnType<typeof loadStripe>;

/** Resolve the Stripe publishable key from env / runtime overrides. */
export function resolveStripePublishableKey(): string {
  // 1) Build-time env
  const fromEnv = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
  if (/^pk_(test|live)_/.test(fromEnv) && fromEnv !== 'pk_test_placeholder') return fromEnv;
  // 2) Runtime global override
  try {
    const g = (window as unknown as { __PUBLIC_STRIPE_KEY__?: unknown }).__PUBLIC_STRIPE_KEY__;
    if (typeof g === 'string' && /^pk_/.test(g)) return g;
  } catch {
    /* ignore */
  }
  // 3) Meta tag
  try {
    const meta = document.querySelector('meta[name="stripe-publishable-key"]') as HTMLMetaElement | null;
    const v = meta?.content || '';
    if (/^pk_/.test(v)) return v;
  } catch {
    /* ignore */
  }
  // 4) LocalStorage cache
  try {
    const ls = localStorage.getItem('stripe_pk') || '';
    if (/^pk_/.test(ls)) return ls;
  } catch {
    /* ignore */
  }
  return '';
}

/** Whether a real (test or live) publishable key is configured. */
export function isStripeConfigured(): boolean {
  const key = resolveStripePublishableKey();
  return /^pk_(test|live)_/.test(key) && key !== 'pk_test_placeholder';
}

let sharedPromise: StripePromise | null = null;

/**
 * Returns the process-wide Stripe.js promise (created once, memoized). The
 * returned reference is stable across calls, so it is safe to pass straight
 * to <Elements stripe={...}> without re-initialising the provider.
 */
export function getSharedStripePromise(): StripePromise {
  if (sharedPromise) return sharedPromise;
  const key = resolveStripePublishableKey();
  const created = loadStripe(isStripeConfigured() ? key : 'pk_test_placeholder');
  sharedPromise = created;
  return created;
}
